import React, { useState } from 'react';
import _ from 'lodash';

import { Badge, Button, Collapse, Drawer, Tooltip } from 'antd';
import { FilterOutlined, ManOutlined, WomanOutlined } from '@ant-design/icons';

import { FILTER_TYPES } from '../../../utils/constants/filter-types';
import { IBVRefinementItem } from '../../models/BVRefinements/IBVRefinementItem';

import './bv-refinement-nav.scss';

import BVBoolean from './components/BVBoolean/bv-boolean';
import BVGender from './components/BVGender/bv-gender';
import BVSearch from './components/BVSearch/bv-search';

const {
    Panel
} = Collapse;

interface IBVRefinementNav {
    dataFilter: IBVRefinementItem[];
    dataFilterLoading?: boolean;
    dataFilterValue: any;
    style?: any;
    onChange: (values: any) => void;
}

const BVRefinementNav: React.FunctionComponent<IBVRefinementNav> = ({ children, dataFilter, dataFilterLoading, dataFilterValue, style, onChange }) => {
    const _handleFilterComponent = (componentSettings: IBVRefinementItem) => {
        switch (componentSettings.type) {
            case FILTER_TYPES.BOOLEAN: {
                return (
                    <BVBoolean
                        className={componentSettings.renderInline ? 'bv-control-inline' : ''}
                        label={componentSettings.renderInline ? '' : componentSettings.label}
                        properties={{
                            loading: dataFilterLoading,
                            disabled: dataFilterLoading,
                            checked: dataFilterValue && dataFilterValue[componentSettings.key],
                            checkedChildren: <Tooltip title={componentSettings.label}>{componentSettings.innerLabel}</Tooltip>,
                            unCheckedChildren: <Tooltip title={componentSettings.label}>{componentSettings.innerLabel}</Tooltip>,
                            onChange: (value: boolean) => onChange({
                                ...dataFilterValue,
                                [componentSettings.key]: value
                            })
                        }}
                    />
                );
            }
            case FILTER_TYPES.GENDER: {
                return (
                    <BVGender
                        className={componentSettings.renderInline ? 'bv-control-inline' : ''}
                        label={componentSettings.renderInline ? '' : componentSettings.label}
                        data={[
                            {
                                label: <Tooltip title='Lady / Female'><WomanOutlined /></Tooltip>,
                                value: 'Lady'
                            },
                            {
                                label: <Tooltip title='Man / Male'><ManOutlined /></Tooltip>,
                                value: 'Man'
                            }
                        ]}
                        properties={{
                            loading: dataFilterLoading,
                            disabled: dataFilterLoading,
                            defaultValue: '',
                            value: dataFilterValue && dataFilterValue[componentSettings.key],
                            onChange: (e: any) => onChange({
                                ...dataFilterValue,
                                [componentSettings.key]: e.target.value
                            })
                        }}
                    />
                );
            }
            case FILTER_TYPES.SEARCH: {
                return (
                    <BVSearch
                        className={componentSettings.renderInline ? 'bv-control-inline' : ''}
                        label={componentSettings.label}
                        properties={{
                            disabled: dataFilterLoading,
                            placeholder: "Search...",
                            onChange: (e: any) => {
                                if (!e.target.value || e.target.value.length > 2) {
                                    onChange({
                                        ...dataFilterValue,
                                        [componentSettings.key]: e.target.value
                                    })
                                }
                            }
                        }}
                    />
                );
            }
            case FILTER_TYPES.GLOBAL_SEARCH: {
                return (
                    <BVSearch
                        className={'bv-global-search'}
                        label={componentSettings.label}
                        properties={{
                            disabled: dataFilterLoading,
                            placeholder: "Search...",
                            value: dataFilterValue && dataFilterValue[componentSettings.key],
                            ref: componentSettings.ref,
                            onChange: (e: any) => {
                                onChange({
                                    display: !!e.target.value,
                                    [componentSettings.key]: e.target.value
                                });
                            }
                        }}
                    />
                );
            }
            default: {
                return null;
            }
        }
    }

    const _handleFilterComponents = (renderInline: boolean = false) => {
        const filterComponents = dataFilter.filter((dataItem: IBVRefinementItem) => (
            dataItem.type !== FILTER_TYPES.SEARCH && dataItem.renderInline === renderInline
        ));

        return _.map(filterComponents, (dataItem: IBVRefinementItem, dataIndex: number) => (
            renderInline
                ?
                <React.Fragment key={dataIndex}>
                    {_handleFilterComponent(dataItem)}
                </React.Fragment>
                :
                <Panel header={dataItem.label} key={dataIndex}>
                    {_handleFilterComponent(dataItem)}
                </Panel>
        ));
    }

    const _handleRenderSearchComponent = (renderInline: boolean = false) => {
        const searchComponentSettings = dataFilter.find((dataItem: IBVRefinementItem) => (
            dataItem.type === FILTER_TYPES.SEARCH && dataItem.renderInline === renderInline
        ));

        if (!searchComponentSettings) {
            return null;
        }

        return _handleFilterComponent(searchComponentSettings);
    }

    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

    return (
        <div className="bv-refinement-nav-container" style={style}>
            {_handleRenderSearchComponent(true)}

            {_handleFilterComponents(true)}

            {_.some(dataFilter, entry => !entry.renderInline) &&
                <Badge dot={false}>
                    <Button
                        type="primary"
                        shape="circle"
                        className="refinement-nav-btn"
                        disabled={dataFilterLoading}
                        icon={<FilterOutlined />}
                        onClick={() => setDrawerVisible(true)}
                    />
                </Badge>
            }

            {children}

            <Drawer
                className="bv-refinement-drawer"
                width={400}
                placement="right"
                closable={true}
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
            >
                <div className="bv-refinement-drawer-container">
                    {_handleRenderSearchComponent()}

                    <Collapse bordered={false} defaultActiveKey={[]}>
                        {_handleFilterComponents()}
                    </Collapse>
                </div>
            </Drawer>
        </div>
    );
}

export default BVRefinementNav;
